/**
 * @file Carries the desktop sign-in request across the `/login` round trip,
 * including the full unload of `signInWithRedirect`. Consumed by
 * `DesktopAuthPage` (save on entry, read before every redirect, clear on
 * success or cancel) and by `LoginPage`, which uses `exists` — never `read` —
 * to pick its return path.
 *
 * sessionStorage, not localStorage: the store is per-tab, so two concurrent
 * desktop attempts in two tabs cannot cross-contaminate. The
 * `socialLoginRedirectPending` localStorage flag is deliberately not the
 * pattern reused here.
 */

import {
  parseDesktopAuthRequest,
  type DesktopAuthRequest,
} from './parseDesktopAuthRequest';

const storageKey = 'desktopAuthRequest';
const ttlMs = 5 * 60 * 1000;

/**
 * Every session-storage touch goes through these two. A browser can refuse the
 * store outright — Safari's private mode and hardened profiles throw on first
 * access rather than answering null — and `LoginPage` calls `exists` on every
 * successful sign-in, so an unguarded throw there would strand every user of
 * such a browser with no route onward. A refused store reads as "no desktop
 * request", which is also the truth: a desktop sign-in cannot survive the
 * redirect without somewhere to record itself.
 */
const readStoredText = (): string | null => {
  try {
    return sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

/** Writes the entry, or removes it when given `null`. Silent when refused. */
const writeStoredText = (value: string | null): void => {
  try {
    if (value === null) sessionStorage.removeItem(storageKey);
    else sessionStorage.setItem(storageKey, value);
  } catch {
    return;
  }
};

const readEntry = (): Record<string, unknown> | undefined => {
  const raw = readStoredText();
  if (raw === null) return undefined;
  const parsed: unknown = (() => {
    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  })();
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  return parsed as Record<string, unknown>;
};

/**
 * `save` anchors `createdAt` to the first sighting of a given `state`: a URL
 * carrying a `state` already in storage keeps the original stamp. Without that,
 * a `/desktop-auth?port&state` entry restored from history or from Chrome's
 * "continue where you left off" would re-stamp itself fresh hours later and
 * mint a live token for a loopback port the agent released long ago. A
 * genuinely new `state` is a genuinely new attempt and starts a new clock,
 * overwriting any stale one.
 *
 * `read` re-runs the full validation on the way out and enforces a TTL that
 * matches the agent's five-minute loopback listener: past that the agent has
 * torn down and its port may already be recycled, so a request older than the
 * TTL is treated as absent and the page renders an error instead of
 * redirecting anything to that port. A `createdAt` in the future is treated the
 * same way rather than as fresh: it is a stamp this clock never issued, so the
 * age it yields is not a measurement of anything. A clock corrected backwards
 * by NTP or a resumed VM pushes expiry out by the size of the correction, and
 * the agent's listener keeps its own timer and does not consult this one.
 *
 * `exists` reports the raw key, expired or corrupt included, so `LoginPage` can
 * send a slow sign-in back to `/desktop-auth` for a loud in-page failure rather
 * than dropping the user on `/` with no explanation.
 */
export const desktopAuthRequestStore = {
  save: (request: DesktopAuthRequest): void => {
    const previous = readEntry();
    const createdAt =
      previous !== undefined &&
      previous.state === request.state &&
      typeof previous.createdAt === 'number'
        ? previous.createdAt
        : Date.now();
    writeStoredText(JSON.stringify({ ...request, createdAt }));
  },
  read: (): DesktopAuthRequest | undefined => {
    const entry = readEntry();
    if (entry === undefined) return undefined;
    const { port, state, createdAt } = entry;
    if (typeof createdAt !== 'number') return undefined;
    const ageMs = Date.now() - createdAt;
    if (ageMs < 0 || ageMs > ttlMs) return undefined;
    return parseDesktopAuthRequest(
      String(port),
      typeof state === 'string' ? state : null,
    );
  },
  exists: (): boolean => readStoredText() !== null,
  clear: (): void => writeStoredText(null),
};
